import { SessionService, FeatureFlagService } from '@nextgen-web-framework/core';
import { Bundle, DerivedAvailabilityStatus, IngredientsDetail, ProductData, ProductDetailInner } from '../services';
import { PreLaunchConfig } from '../services/models/pdp/models/PreLaunchConfig';
import { DerivedAvailabilityStatusInternal } from '../transformers/derivedBundleStatusTransformer';
import { DerivedAvailabilityStatusWithPreLaunch, ProductType, type IProduct, type PreLaunchValues } from '../types';
import { hasRequiredFields } from '../utils/productSDKHelper';

const { session } = SessionService.getSession();

const checkForAvailableDate = (date: Date | undefined): string => (date ? new Date(date)?.toISOString() : '');

const getPreLaunchValues = (
  preLaunchConfig: PreLaunchConfig & Required<Pick<PreLaunchConfig, 'generalAvailDateTime'>>,
  beforePreLaunchValue: {
    date: string;
    stockStatus: DerivedAvailabilityStatus;
  },
): {
  stockStatus: DerivedAvailabilityStatusWithPreLaunch;
  date: string;
} => {
  const { generalAvailDateTime, accessSettings = [] } = preLaunchConfig;
  const { currentAwardLevel = '0' } = session;
  const currentUTCTime = new Date().getTime();
  const filteredAccessSettings = accessSettings.filter(
    (accessSetting) => accessSetting.awardLevelAccess !== undefined && accessSetting.startDateTime != undefined,
  );
  filteredAccessSettings.sort((a, b) => b.awardLevelAccess! - a.awardLevelAccess!);

  const userAccessSetting = filteredAccessSettings.find(
    ({ awardLevelAccess = Number.MAX_SAFE_INTEGER }) => +currentAwardLevel >= awardLevelAccess,
  );

  const hasHigherPriorityThanExclusiveForYou = (
    [DerivedAvailabilityStatus.Nya, DerivedAvailabilityStatus.Bo] as DerivedAvailabilityStatus[]
  ).includes(beforePreLaunchValue.stockStatus);

  const startDateTime = userAccessSetting?.startDateTime ?? generalAvailDateTime;
  const startUTCTime = new Date(startDateTime).getTime();
  // period-1 condition
  if (currentUTCTime < startUTCTime) {
    return { stockStatus: DerivedAvailabilityStatus.Nya, date: checkForAvailableDate(startDateTime) };
    // period-2 condition
  } else {
    return hasHigherPriorityThanExclusiveForYou
      ? beforePreLaunchValue
      : { stockStatus: DerivedAvailabilityStatusInternal.ExclusiveForYou, date: '' };
  }
};

const getDerivedAvailabilityStatus = (productData: ProductData): DerivedAvailabilityStatus => {
  if (checkIfBundle(productData)) {
    return productData?.bundle?.derivedAvailabilityStatus as DerivedAvailabilityStatus;
  } else {
    return productData?.items?.[0]?.inventory?.[0]?.derivedAvailabilityStatus as DerivedAvailabilityStatus;
  }
};

const getFirstAvailableFutureDate = (productData: ProductData): string => {
  if (checkIfBundle(productData)) {
    return checkForAvailableDate(productData?.bundle?.derivedFirstAvailableFutureDate);
  } else {
    return checkForAvailableDate(productData?.items?.[0]?.inventory?.[0]?.firstAvailableFutureDate);
  }
};

const derivedAvailabilityStatusAndFutureDateHandler = (
  productData: ProductData,
  showPreLaunch = true,
): PreLaunchValues => {
  const { preLaunchConfig = {} } = productData || {};
  const currentUTCDate = new Date().getTime();
  const ifProductSetForPreLaunch = Object.keys(preLaunchConfig).length > 0 && productData?.flags?.isPreLaunchActive;
  const beforePreLaunchValues = {
    date: getFirstAvailableFutureDate(productData),
    stockStatus: getDerivedAvailabilityStatus(productData),
  };
  if (
    showPreLaunch &&
    ifProductSetForPreLaunch &&
    !([DerivedAvailabilityStatus.Nla, DerivedAvailabilityStatus.OutOfStock] as DerivedAvailabilityStatus[]).includes(
      beforePreLaunchValues.stockStatus,
    ) &&
    hasRequiredFields(preLaunchConfig, 'generalAvailDateTime') &&
    // period-3 condition for preLaunch
    currentUTCDate < new Date(preLaunchConfig.generalAvailDateTime).getTime()
  ) {
    return getPreLaunchValues(preLaunchConfig, beforePreLaunchValues);
  } else {
    return beforePreLaunchValues;
  }
};

const updateBundleForPreLaunch = (bundle: Bundle | undefined, preLaunchValues: PreLaunchValues) => {
  if (bundle) {
    const updatedBundle = {
      ...bundle,
      derivedAvailabilityStatus: preLaunchValues['stockStatus'] as DerivedAvailabilityStatus,
    };
    if (hasRequiredFields(preLaunchValues, 'date')) {
      updatedBundle.derivedFirstAvailableFutureDate = new Date(preLaunchValues['date']);
    }
    return updatedBundle;
  }
  return bundle;
};

const getProductId = (productData: ProductData): string =>
  checkIfBundle(productData) ? productData?.id : (productData?.items?.[0]?.itemNumber as string);

const checkIfBundle = (productData: ProductData): boolean => {
  const { type, flags } = productData || {};
  return !!(type === ProductType.Bundle || flags?.isBundle);
};

const getCurrency = (productData: ProductData): string =>
  checkIfBundle(productData)
    ? (productData?.bundle?.currency as string)
    : (productData?.items?.[0]?.prices?.[0]?.currency as string);

const getBusinessVolume = (data: ProductData): string => {
  let value = '';
  if (checkIfBundle(data)) {
    if (hasRequiredFields(data, 'bundle')) {
      const { minBusinessVolume, maxBusinessVolume } = data?.bundle as Bundle;
      if (minBusinessVolume !== maxBusinessVolume) {
        value = `${minBusinessVolume}-${maxBusinessVolume}`;
      } else {
        value = maxBusinessVolume?.toString() || '';
      }
    }
  } else {
    value = data?.items?.[0]?.prices?.[0]?.businessVolume?.toString() || '';
  }
  return value;
};

const getPointValue = (data: ProductData): string => {
  let value = '';
  if (checkIfBundle(data)) {
    if (hasRequiredFields(data, 'bundle')) {
      const { minPointValue, maxPointValue } = data?.bundle as Bundle;
      if (minPointValue !== maxPointValue) {
        value = `${minPointValue}-${maxPointValue}`;
      } else {
        value = maxPointValue?.toString() || '';
      }
    }
  } else {
    value = data?.items?.[0]?.prices?.[0]?.pointValue?.toString() || '';
  }
  return value;
};

const productDataTransformer = (productData: ProductData, showPreLaunch = true): IProduct => {
  const {
    gallery,
    copy,
    items,
    variantGroup: variantData,
    bundle,
    type,
    flags,
    defaultCategoryId,
    variantGroupId,
    tags = [{}],
    amwayLabels,
    attributes,
    categories,
    hazardAttributes,
    aliasCode,
    manufacturer,
    name,
    countryCode,
    tenant,
    vertical,
    locale,
    onlineSince,
    variantDimensions,
    version,
    metadata,
    pcmMetadata,
    liveFrom,
    publishStatus,
    extensions,
    pdpLayoutJson,
    id,
  } = productData || {};
  const isPreLaunchFeatureVisible = FeatureFlagService.getFeatureVisibility('product.showPreLaunch');
  const preLaunchValues = derivedAvailabilityStatusAndFutureDateHandler(productData, showPreLaunch);
  return {
    galleryList: gallery?.media || [],
    brandName: copy?.brandName || null,
    productName: copy?.title || '',
    alt: gallery?.media?.[0]?.thumbnail?.altText || '',
    pointValue: getPointValue(productData),
    businessVolume: getBusinessVolume(productData),
    productPrice: items?.[0]?.prices?.[0]?.priceIncludingTax as number,
    currency: getCurrency(productData),
    productId: getProductId(productData),
    id: id,
    productSize: copy?.servingSize || '',
    faqs: copy?.faqs || [],
    shortDescription: copy?.summary || '',
    variantData,
    defaultVariantItemId: items?.[0]?.itemNumber || '',
    ingredientsData: {
      ingredientsDetail: copy?.ingredientsDetail as IngredientsDetail,
      ingredients: copy?.ingredients || '',
    },
    productDetail: copy?.productDetail?.[0] as ProductDetailInner,
    isBundle: checkIfBundle(productData),
    minPrice: bundle?.minPrice as number,
    maxPrice: bundle?.maxPrice as number,
    productRetailPrice: items?.[0]?.prices?.[0]?.retailIncludingTax as number,
    minRetailPrice: bundle?.minRetailPrice,
    maxRetailPrice: bundle?.maxRetailPrice,
    bundle: isPreLaunchFeatureVisible && showPreLaunch ? updateBundleForPreLaunch(bundle, preLaunchValues) : bundle,
    instructions: copy?.instructions,
    derivedAvailabilityStatus:
      isPreLaunchFeatureVisible && showPreLaunch
        ? preLaunchValues['stockStatus']
        : getDerivedAvailabilityStatus(productData),
    firstAvailableFutureDate:
      isPreLaunchFeatureVisible && showPreLaunch ? preLaunchValues['date'] : getFirstAvailableFutureDate(productData),
    mainLink: gallery?.media?.[0]?.main?.publicUrl || '',
    productType: type,
    productFlags: flags,
    defaultCategoryId,
    variantGroupId: variantGroupId || '',
    productTags: { ...tags[0] },
    amwayLabels: amwayLabels || [],
    attributes,
    categories: categories || [],
    hazardAttributes,
    aliasCode: aliasCode || '',
    items: items || [],
    manufacturer: manufacturer || '',
    systemName: name || '',
    countryCode: countryCode || '',
    tenant: tenant || '',
    vertical: vertical || '',
    locale: locale || '',
    onlineSince,
    variantDimensions: variantDimensions || [],
    version,
    metadata,
    pcmMetadata,
    liveFrom,
    publishStatus,
    extensions,
    pdpLayoutJson,
    aPointPrice: extensions?.['x-th-aPointPrice'] ? (extensions['x-th-aPointPrice'] as unknown as number) : undefined, // Backend API returns extensions values as object hence needed type conversion
    aPointPriceCompanionText: extensions?.['x-th-aPointPriceAddedText']
      ? (extensions['x-th-aPointPriceAddedText'] as unknown as string)
      : undefined,
    aPointConditionsText: extensions?.['x-th-aPointConditionsText']
      ? (extensions['x-th-aPointConditionsText'] as unknown as string)
      : undefined,
    seoTitle: copy?.seoTitle || '',
    seoDescription: copy?.seoDescription || '',
    seoKeywords: copy?.seoKeywords || '',
  };
};

export { productDataTransformer, derivedAvailabilityStatusAndFutureDateHandler };
